/**
 * Credits are the customer's unit (D-CP21). Every wallet, ledger and rating figure travels as INTEGER
 * micro-credits; a "displayed credit" is `micro ÷ credit_display_scale` (billing settings, default 100).
 *
 * These helpers are READ transforms only. A displayed figure is never stored, never sent back, and never
 * used to decide anything — the server's micro-credits are the truth. When the scale is unknown (billing
 * unavailable), the raw micro-credits are shown and labelled as such rather than divided by a guess.
 */

/** A usable display scale: a positive safe integer. Anything else means "unknown". */
export function isValidScale(scale: unknown): scale is number {
  return typeof scale === 'number' && Number.isSafeInteger(scale) && scale >= 1;
}

/** How many decimals a displayed credit can carry at this scale (100 → 2, 1 → 0, 250 → 3). */
export function displayDecimals(scale: number): number {
  if (!isValidScale(scale) || scale === 1) return 0;
  // The digit count of (scale − 1): 100 → "99" → 2, 1000 → 3, 250 → 3. Integer-only, so no
  // floating-point log10 edge at the powers of ten.
  return String(scale - 1).length;
}

/** Displayed credits for a micro-credit amount. Null in → null out; an unknown scale → null (never a guess). */
export function microToDisplayed(micro: number | null | undefined, scale: number | null | undefined): number | null {
  if (micro === null || micro === undefined || !Number.isFinite(micro)) return null;
  if (!isValidScale(scale)) return null;
  return micro / scale;
}

/**
 * `12345` µ at scale 100 → `"123.45 credits"`. Null → `"—"` (not collected is never zero).
 * Unknown scale → `"12,345 micro-credits"`, explicitly labelled, so a figure is never divided by a
 * scale nobody confirmed.
 */
export function formatCredits(
  micro: number | null | undefined,
  scale: number | null | undefined,
  options: { unit?: boolean } = {},
): string {
  const unit = options.unit ?? true;
  if (micro === null || micro === undefined || !Number.isFinite(micro)) return '—';

  if (!isValidScale(scale)) {
    // Always labelled, even when the caller asked for a bare number: an unlabelled raw figure would read as credits,
    // 100× (the usual scale) too large — the exact "assumed scale" error this transform exists to prevent.
    return `${Math.round(micro).toLocaleString('en-US')} micro-credits`;
  }

  const displayed = micro / scale;
  const text = displayed.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: displayDecimals(scale),
  });
  return unit ? `${text} ${Math.abs(displayed) === 1 ? 'credit' : 'credits'}` : text;
}

/**
 * Parses a DISPLAYED credit amount typed by an admin into micro-credits (what every wallet endpoint
 * takes — docs/api-tenant-ops.md §4.5).
 *
 * String arithmetic, not `Number(x) * scale`: `0.29 * 100` is `28.999999999999996` in floating point,
 * and a grant must never be off by one micro-credit. A value that does not land on a whole micro-credit
 * at this scale is refused rather than rounded.
 */
export function parseDisplayedCredits(
  input: string,
  scale: number | null | undefined,
): { ok: true; micro: number } | { ok: false; error: string } {
  if (!isValidScale(scale)) {
    return { ok: false, error: 'The credit display scale is unavailable, so a displayed amount cannot be converted.' };
  }

  const text = input.trim().replace(/,/g, '');
  if (text === '') return { ok: false, error: 'Enter an amount.' };

  const match = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return { ok: false, error: 'Enter a non-negative number of credits.' };

  const whole = Number(match[1]);
  const fraction = match[2] ?? '';
  const denominator = 10 ** fraction.length;
  const fractionValue = fraction === '' ? 0 : Number(fraction);
  const scaledFraction = fractionValue * scale;

  if (scaledFraction % denominator !== 0) {
    return {
      ok: false,
      error: `Too precise for the display scale (1 credit = ${scale.toLocaleString('en-US')} micro-credits): ` +
        `use at most ${displayDecimals(scale)} decimal place(s).`,
    };
  }

  const micro = whole * scale + scaledFraction / denominator;
  if (!Number.isSafeInteger(micro)) return { ok: false, error: 'That amount is too large.' };
  return { ok: true, micro };
}

/**
 * The money equivalent line that sits under a credit figure. Only ever built from a server-provided
 * amount (`price_minor` on a rated line): the console never re-derives a price from the price book — that
 * is billing's one resolver (BL6).
 */
export const MONEY_EQUIVALENT_LABEL = 'at current pack price';

/**
 * The scale itself in words: `"100"`, or `"unknown"` when a service answered null (billing unreachable when it asked) —
 * never a silent 100 (money review 2026-10-04). Use as `1 credit = {scaleText(s)} micro-credits`.
 */
export function scaleText(scale: number | null | undefined): string {
  return isValidScale(scale) ? scale.toLocaleString('en-US') : 'unknown';
}

/** Micro-credits per ONE unit of the credit reference currency (D-CP21: 100,000 µ = 1 USD). */
export const MICRO_CREDITS_PER_REFERENCE_UNIT = 100_000;

/**
 * Money (integer minor units of `currency`) → micro-credits at an FX rate (`units_per_reference` = units of `currency`
 * per 1 reference unit), rounded UP like billing's `MicroCreditsFromMoney`, so a cost is never understated. Null when
 * the rate is unusable. `digits` = the currency's minor digits (billing's table).
 */
export function moneyToMicroCredits(minor: number, digits: number, unitsPerReference: number | null | undefined): number | null {
  if (!Number.isFinite(minor) || !unitsPerReference || !Number.isFinite(unitsPerReference) || unitsPerReference <= 0) return null;
  const value = (minor * MICRO_CREDITS_PER_REFERENCE_UNIT) / (10 ** digits * unitsPerReference);
  // Strip binary noise before the ceiling (0.1 + 0.2 must not round up to the next micro-credit).
  return Math.ceil(Number(value.toFixed(6)));
}
